import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import uniq from 'lodash-es/uniq';
import { AlertTriangleIcon, CircleHelpIcon, InfoIcon } from 'lucide-react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { Chips } from '@/components/atoms/Chip/Chip';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AutocompleteOption,
  FormAutocomplete,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import { Form, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import {
  selectAllTags,
  selectNodeIdByActionId,
  selectSelectedAction,
} from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { MAX_CONVERSATION_TAGS } from '@/util/constants';
import { sanitizeTagName } from '@/util/util';
import { newTagSchema } from '@/util/validation/schemas/dialog-schemas';

import ToolkitWrapper from '../../ToolkitWrapper';

type FormType = { tag: string };
const ALLOWED_OPERATIONS = ['add', 'remove', 'clear'] as const;

const ToolkitActionTag = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { actionId, tags, operation, type } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as Extract<Action, { type: 'tag' }>;
    return {
      actionId: selectedAction?.action_id,
      tags: selectedAction?.type === 'tag' ? selectedAction.tags : [],
      operation: selectedAction?.operation || 'add',
      type: selectedAction?.type,
    };
  }, shallowEqual);
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

  const brainId = useSelector(selectBrainId);
  const { dialogs } = useDialogs(brainId);
  const allTags = selectAllTags(dialogs);
  const suggestedTags = allTags.filter((tag) => !tags.includes(tag));

  const autocompleteOptions = useMemo<AutocompleteOption[]>(
    () =>
      suggestedTags?.map((tag) => ({
        category: t('dialog.tags.auto_complete_group_by'),
        value: tag,
        label: tag,
      })) ?? [],
    [suggestedTags, t]
  );

  const form = useForm<FormType>({
    mode: 'onChange',
    defaultValues: { tag: '' },
    resolver: yupResolver(newTagSchema) as Resolver<FormType>,
  });

  const { control, trigger, setValue } = form;

  const saveNewTag = useCallback(
    (tag: string) => {
      dispatch(updateAction({ actionId, action: { tags: uniq([...tags, tag]) } }));
    },
    [actionId, dispatch, tags]
  );

  const handleOnTagDelete = useCallback(
    (deletedTag: string) => {
      dispatch(
        updateAction({ actionId, action: { tags: tags.filter((tag) => tag !== deletedTag) } })
      );
    },
    [actionId, dispatch, tags]
  );

  const handleOperationChange = useCallback(
    (value: string) => {
      let newTags = tags;
      if (value === 'clear') {
        newTags = [];
      } else if (tags.length === 0) {
        newTags = [t('dialog.pre-fill.tag')];
      }
      dispatch(updateAction({ actionId, action: { operation: value, tags: newTags } }));
    },
    [actionId, dispatch, t, tags]
  );

  const handleAddTag = useCallback(
    (value: string) => {
      if (!value) {
        return;
      }
      const sanitized = sanitizeTagName(value);
      if (sanitized && newTagSchema.isValidSync({ tag: sanitized })) {
        saveNewTag(sanitized);
      }
      setValue('tag', '');
    },
    [saveNewTag, setValue]
  );

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t('actions.types.tag'),
    type,
  });

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    const body =
      tags?.length === 0 && operation !== 'clear'
        ? t('dialog.errors.at_least_one', { 0: t('actions.types.tag').toLowerCase() })
        : undefined;
    setFieldError('tag', body);
  }, [t, tags?.length, operation, setFieldError]);

  return (
    <ToolkitWrapper type="tag">
      <Form {...form}>
        <div className="flex flex-col gap-4 pb-6 [&_p]:whitespace-normal">
          <RadioGroup value={operation} onValueChange={handleOperationChange} className="gap-6">
            {ALLOWED_OPERATIONS.map((o) => (
              <div key={o}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value={o} id={`tag-op-${o}`} />
                  <label htmlFor={`tag-op-${o}`} className="cursor-pointer text-sm font-medium">
                    {t(`dialog.tags.${o}_tags`)}
                  </label>
                </div>

                {operation === o && o !== 'clear' && (
                  <div className="mt-4">
                    <FormField
                      control={control}
                      name="tag"
                      render={() => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-1">
                            {t('common.tags')}
                            <InfoTooltip content={t('dialog.tags.tags_tooltip')} size="large">
                              <CircleHelpIcon className="size-3.5 text-foreground-muted" />
                            </InfoTooltip>
                          </FormLabel>
                          <FormAutocomplete
                            name="tag"
                            control={control}
                            options={autocompleteOptions}
                            placeholder={t('dialog.tags.select_tag')}
                            disabled={tags?.length >= MAX_CONVERSATION_TAGS}
                            onChange={handleAddTag}
                            onCreateNew={handleAddTag}
                            createNewText={(search) => {
                              const sanitized = sanitizeTagName(search);
                              return sanitized ? t('common.add_new', { 0: sanitized }) : '';
                            }}
                          />
                        </FormItem>
                      )}
                    />

                    <Chips
                      className="mt-2"
                      chips={tags}
                      onDelete={(tag) => handleOnTagDelete(tag)}
                    />

                    {tags?.length === MAX_CONVERSATION_TAGS && (
                      <div className="mt-6">
                        <Alert variant="info">
                          <InfoIcon size={16} />
                          <AlertDescription>{t('dialog.tags.warning')}</AlertDescription>
                        </Alert>
                      </div>
                    )}

                    {tags?.length === 0 && (
                      <div className="mt-6">
                        <Alert variant="destructive">
                          <AlertTriangleIcon size={16} />
                          <AlertDescription>
                            {t('dialog.errors.at_least_one', {
                              0: t('actions.types.tag').toLowerCase(),
                            })}
                          </AlertDescription>
                        </Alert>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </RadioGroup>
        </div>
      </Form>
    </ToolkitWrapper>
  );
};

export default ToolkitActionTag;
